import React from 'react';
import { motion } from 'framer-motion';
import { GraduationCap, BookOpen, Award, Wrench, Briefcase, HeartHandshake, LayoutGrid } from 'lucide-react';

const PROGRAMS = [
  {
    Icon: GraduationCap,
    color: 'text-brand-indigo',
    bg: 'from-brand-indigo/12 to-brand-indigo/5',
    accent: 'bg-brand-indigo',
    title: 'Merit-Based Scholarships',
    desc: 'Awarding financial grants to outstanding students from low-income families, enabling them to pursue higher education without economic constraints.',
  },
  {
    Icon: BookOpen,
    color: 'text-brand-emerald',
    bg: 'from-brand-emerald/12 to-brand-emerald/5',
    accent: 'bg-brand-emerald',
    title: 'Academic Resource Program',
    desc: 'Distributing vital learning materials including books, stationery, and modern educational technology to support student success.',
  },
  {
    Icon: Award,
    color: 'text-brand-amber',
    bg: 'from-brand-amber/12 to-brand-amber/5',
    accent: 'bg-brand-amber',
    title: 'Excellence Recognition',
    desc: 'Celebrating outstanding academic performance through awards and recognition programs that inspire continued educational achievement.',
  },
  {
    Icon: Wrench,
    color: 'text-brand-indigo',
    bg: 'from-brand-indigo/12 to-brand-indigo/5',
    accent: 'bg-brand-indigo',
    title: 'Vocational Skills Training',
    desc: 'Hands-on training in electrical work, plumbing, tailoring, computer basics, and other trades to enhance employability.',
  },
  {
    Icon: Briefcase,
    color: 'text-brand-emerald',
    bg: 'from-brand-emerald/12 to-brand-emerald/5',
    accent: 'bg-brand-emerald',
    title: 'Career & Apprenticeship Pathways',
    desc: 'Connecting learners with industry-recognised certifications, internships, and apprenticeships for sustainable career development.',
  },
  {
    Icon: HeartHandshake,
    color: 'text-brand-amber',
    bg: 'from-brand-amber/12 to-brand-amber/5',
    accent: 'bg-brand-amber',
    title: 'Ally – Vocational Services',
    desc: 'Partnering with Health & ITI service providers to promote well-being, counselling support, and community-based care for individuals and families.',
  },
];

const containerVar = {
  hidden:  { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.08, delayChildren: 0.12 } },
};
const cardVar = {
  hidden:  { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] } },
};
const fadeUp = { hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0, transition: { duration: 0.55 } } };

const ProgramCard = ({ Icon, color, bg, accent, title, desc }) => (
  <motion.div
    variants={cardVar}
    whileHover={{ y: -7, scale: 1.015 }}
    transition={{ type: 'spring', stiffness: 300, damping: 22 }}
    className="group relative glassmorphism-card rounded-2xl p-7 border border-border/50
               hover:shadow-xl hover:border-primary/15 transition-all duration-300
               overflow-hidden flex flex-col"
  >
    {/* Colored top accent bar */}
    <div className={`absolute top-0 left-0 right-0 h-[3px] ${accent} rounded-t-2xl
                     opacity-70 group-hover:opacity-100 transition-opacity duration-300`} />

    {/* Icon */}
    <div className={`inline-flex p-4 rounded-2xl bg-gradient-to-br ${bg} mb-5 self-start
                     group-hover:scale-110 transition-transform duration-300`}>
      <Icon className={`h-7 w-7 ${color}`} />
    </div>

    {/* Text */}
    <h3 className="text-[15px] font-bold text-foreground mb-3 leading-snug tracking-tight">{title}</h3>
    <p className="text-sm text-muted-foreground leading-relaxed flex-1">{desc}</p>

    {/* Subtle hover glow */}
    <div className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500
                    pointer-events-none"
         style={{ background: 'radial-gradient(circle at 50% 0%, hsl(var(--primary)/0.04) 0%, transparent 60%)' }} />
  </motion.div>
);

const ProgramsSection = () => (
  <section id="programs" className="py-20 md:py-28 bg-muted/25">
    <div className="container mx-auto px-4 sm:px-6">

      {/* Badge + Header */}
      <motion.div
        className="text-center mb-16"
        initial="hidden"
        whileInView="visible"
        variants={fadeUp}
        viewport={{ once: true }}
      >
        <div className="flex justify-center mb-4">
          <span className="section-badge-emerald">
            <LayoutGrid className="h-3 w-3" />
            What We Offer
          </span>
        </div>
        <h2 className="text-3xl md:text-4xl font-bold mb-5">
          Our <span className="gradient-text">Programs</span>
        </h2>
        <p className="text-muted-foreground text-lg max-w-2xl mx-auto leading-relaxed">
          Committed to transforming lives through comprehensive educational assistance and student empowerment.
        </p>
      </motion.div>

      <motion.div
        variants={containerVar}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.08 }}
        className="grid md:grid-cols-2 lg:grid-cols-3 gap-5"
      >
        {PROGRAMS.map((p) => (
          <ProgramCard key={p.title} {...p} />
        ))}
      </motion.div>
    </div>
  </section>
);

export default ProgramsSection;
